import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Star, Heart, Minus, Plus, Truck, RefreshCw, Check } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/seo/SEO';
import ProductCarousel from '../components/products/ProductCarousel';
import ProductGridSkeleton from '../components/products/ProductGridSkeleton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../context/CartContext';
import { formatCurrency } from '../lib/currency';
import { getProductById, getRelatedProducts } from '../services/products';
import { getCategoryById, getCategories } from '../services/categories';
import { getSizeGuideById } from '../services/sizeGuides';
import MobileStickyBar from '../components/layout/MobileStickyBar';
import { BRAND_NAME } from '../lib/brand';

const ProductDetailPage = () => {
  const { t } = useTranslation();
  const { productId } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, isInWishlist } = useCart();

  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [carouselApi, setCarouselApi] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [sizeGuide, setSizeGuide] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const productData = await getProductById(productId);
        setProduct(productData);

        // Set default color
        if (productData.colorNames && productData.colorNames.length > 0) {
          setSelectedColor(productData.colorNames[0]);
        }

        // Fetch category
        if (productData.category) {
          try {
            const categoryData = await getCategoryById(productData.category);
            setCategory(categoryData);
          } catch (error) {
            console.error('Error fetching category:', error);
            // Set fallback category object instead of blocking page render
            setCategory({
              id: productData.category,
              name: 'Genel',
              slug: 'genel',
            });
          }
        } else {
          // No category set, use fallback
          setCategory({
            id: null,
            name: 'Genel',
            slug: 'genel',
          });
        }

        // Fetch related products
        if (productData.category) {
          try {
            const related = await getRelatedProducts(productId, productData.category, 8);
            setRelatedProducts(related);
          } catch (error) {
            console.error('Error fetching related products:', error);
          }
        }

        if (productData.sizeGuideId) {
          try {
            const guide = await getSizeGuideById(productData.sizeGuideId);
            setSizeGuide(guide);
          } catch (error) {
            console.error('Error fetching size guide:', error);
            setSizeGuide(null);
          }
        } else {
          setSizeGuide(null);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast({
          title: 'Hata',
          description: 'Ürün bulunamadı.',
          variant: 'destructive',
        });
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId, navigate, toast]);

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => {
      setSelectedImage(carouselApi.selectedScrollSnap());
    };
    carouselApi.on('select', onSelect);
    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi]);

  if (loading) {
    return (
      <Layout>
        <SEO title={`${BRAND_NAME} | Ürün`} description="Ürün detayları yükleniyor..." />
        <div className="lg:max-w-7xl lg:mx-auto lg:px-8 lg:py-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12">
            <div className="relative w-screen max-w-[100vw] -translate-x-1/2 left-1/2 lg:relative lg:left-0 lg:w-full lg:max-w-none lg:translate-x-0">
              <Skeleton className="product-detail-gallery w-full rounded-none lg:aspect-[3/4] lg:h-auto lg:max-h-[85vh]" />
            </div>
            <div className="space-y-6 px-4 pt-6 sm:px-6 lg:px-0">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-10 w-1/3" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <SEO title={`${BRAND_NAME} | Ürün Bulunamadı`} description="Ürün bulunamadı" />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-semibold mb-4">{t('errors.notFound')}</h1>
          <Button onClick={() => navigate('/')}>{t('common.home')}</Button>
        </div>
      </Layout>
    );
  }

  const inWishlist = isInWishlist(product.id);
  const categoryName = category?.name || product.category;

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast({
        title: t('product.selectSize'),
        description: t('product.selectSize'),
        variant: 'destructive',
      });
      return;
    }

    addToCart(product, selectedSize, selectedColor, quantity);
    toast({
      title: t('product.addedToCart'),
      description: `${product.name} ${t('product.addedToCart')}`,
    });
  };

  const handleWishlist = () => {
    toggleWishlist(product);
    toast({
      title: inWishlist ? t('product.removedFromWishlist') : t('product.addedToWishlist'),
      description: inWishlist
        ? `${product.name} ${t('product.removedFromWishlist')}`
        : `${product.name} ${t('product.addedToWishlist')}`,
    });
  };

  const renderStars = (rating) => {
    const numRating = rating || 0;
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={i < Math.floor(numRating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
      />
    ));
  };

  const discountPercent = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;
  const descriptionHtml = (product.description || 'Açıklama bulunmamaktadır.').replace(
    /\n/g,
    '<br />'
  );

  return (
    <Layout>
      <SEO
        title={`${BRAND_NAME} | ${product.name}`}
        description={product.description || `${product.name} - ${BRAND_NAME} kalitesiyle sizlerle. Hızlı kargo ve güvenli alışveriş.`}
        image={product.images?.[0]}
      />
      <div className="pb-28 lg:pb-8">
        <div className="lg:mx-auto lg:max-w-7xl lg:px-8 lg:pt-8">
          <nav className="mb-8 hidden items-center overflow-x-auto text-sm text-gray-500 lg:flex">
            <Link to="/" className="whitespace-nowrap transition-colors hover:text-black">
              {t('common.home')}
            </Link>
            <ChevronRight size={14} className="mx-2 flex-shrink-0" />
            {category && (
              <>
                <Link
                  to={`/category/${product.category}`}
                  className="whitespace-nowrap transition-colors hover:text-black"
                >
                  {categoryName}
                </Link>
                <ChevronRight size={14} className="mx-2 flex-shrink-0" />
              </>
            )}
            <span className="truncate font-medium text-black">{product.name}</span>
          </nav>

          <div className="lg:grid lg:grid-cols-2 lg:gap-12">
            {/* Full-bleed image gallery */}
            <div className="relative w-screen max-w-[100vw] -translate-x-1/2 left-1/2 lg:relative lg:left-0 lg:w-full lg:max-w-none lg:translate-x-0">
              <div className="product-detail-gallery relative w-full overflow-hidden bg-gray-100 lg:aspect-[3/4] lg:h-auto lg:max-h-[85vh]">
                {product.images && product.images.length > 0 ? (
                  <Carousel setApi={setCarouselApi} className="product-detail-carousel absolute inset-0 h-full w-full">
                    <CarouselContent className="-ml-0 h-full [&>div]:h-full">
                      {product.images.map((image, index) => (
                        <CarouselItem key={index} className="h-full basis-full pl-0">
                          <div className="relative h-full w-full overflow-hidden">
                            <img
                              src={image}
                              alt={`${product.name} ${index + 1}`}
                              loading={index === 0 ? 'eager' : 'lazy'}
                              draggable={false}
                            />
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {product.images.length > 1 && (
                      <>
                        <CarouselPrevious className="left-3 top-1/2 h-9 w-9 -translate-y-1/2 border-0 bg-white/80 shadow-md backdrop-blur-sm hover:bg-white lg:left-4" />
                        <CarouselNext className="right-3 top-1/2 h-9 w-9 -translate-y-1/2 border-0 bg-white/80 shadow-md backdrop-blur-sm hover:bg-white lg:right-4" />
                      </>
                    )}
                  </Carousel>
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-gray-400">
                    Resim Yok
                  </div>
                )}

                <div className="absolute left-3 top-3 z-10 flex flex-col gap-1.5 lg:left-4 lg:top-4">
                  {product.onSale && (
                    <Badge className="border-0 bg-red-500/55 text-white backdrop-blur-sm">
                      {t('common.sale')}
                    </Badge>
                  )}
                  {product.isNew && (
                    <Badge className="border-0 bg-blue-600/55 text-white backdrop-blur-sm">
                      {t('product.new')}
                    </Badge>
                  )}
                </div>

                {product.images && product.images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
                    {product.images.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => carouselApi?.scrollTo(index)}
                        className={`h-1.5 rounded-full transition-all ${
                          selectedImage === index ? 'w-4 bg-white' : 'w-1.5 bg-white/50'
                        }`}
                        aria-label={`Görsel ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-6 px-4 pt-4 sm:px-6 lg:sticky lg:top-28 lg:self-start lg:px-0 lg:pt-0">
              <nav className="mb-2 flex items-center overflow-x-auto text-sm text-gray-500 lg:hidden">
                <Link to="/" className="whitespace-nowrap transition-colors hover:text-black">
                  {t('common.home')}
                </Link>
                <ChevronRight size={14} className="mx-2 flex-shrink-0" />
                {category && (
                  <>
                    <Link
                      to={`/category/${product.category}`}
                      className="whitespace-nowrap transition-colors hover:text-black"
                    >
                      {categoryName}
                    </Link>
                    <ChevronRight size={14} className="mx-2 flex-shrink-0" />
                  </>
                )}
                <span className="truncate font-medium text-black">{product.name}</span>
              </nav>
            {/* Title & Rating */}
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold mb-2">{product.name}</h1>
              {(product.rating || product.reviews) && (
                <div className="flex items-center gap-2">
                  <div className="flex">{renderStars(product.rating)}</div>
                  <span className="text-sm text-gray-600">
                    {product.rating?.toFixed(1) || '0'} ({product.reviews || 0} {t('product.reviews')})
                  </span>
                </div>
              )}
            </div>

            {/* Price */}
            <div className="flex items-center gap-3">
              <span className="text-2xl font-semibold">{formatCurrency(product.price)}</span>
              {product.originalPrice && (
                <>
                  <span className="text-xl text-gray-500 line-through">
                    {formatCurrency(product.originalPrice)}
                  </span>
                  <Badge variant="secondary" className="bg-red-100 text-red-600">
                    %{discountPercent} {t('common.sale')}
                  </Badge>
                </>
              )}
            </div>

            <Separator />

            {/* Color Selection */}
            {product.colors && product.colors.length > 0 && (
              <div>
                <h3 className="font-medium mb-3">
                  {t('product.color')}: {selectedColor || product.colorNames?.[0]}
                </h3>
                <div className="flex gap-2">
                  {product.colors.map((color, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedColor(product.colorNames?.[index] || '')}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${
                        selectedColor === (product.colorNames?.[index] || '') ||
                        (!selectedColor && index === 0)
                          ? 'border-black scale-110'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: color }}
                      title={product.colorNames?.[index]}
                      aria-label={product.colorNames?.[index]}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection */}
            {product.sizes && product.sizes.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium">{t('product.size')}</h3>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-sm text-blue-600 hover:underline">
                        {t('product.sizeGuide')}
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>{t('product.sizeGuide')}</DialogTitle>
                      </DialogHeader>
                      <div className="mt-4">
                        {sizeGuide ? (
                          <div
                            className="text-sm text-gray-700 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: sizeGuide.content }}
                          />
                        ) : (
                          <p className="text-sm text-gray-600">Bu ürün için beden rehberi yok.</p>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 border rounded-md text-sm font-medium transition-all ${
                        selectedSize === size
                          ? 'bg-black text-white border-black'
                          : 'border-gray-300 hover:border-black'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div>
              <h3 className="font-medium mb-3">{t('product.quantity')}</h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-10 h-10 border rounded-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                  aria-label={t('product.quantity')}
                >
                  <Minus size={16} />
                </button>
                <span className="w-12 text-center font-medium">{quantity}</span>
                <button
                  onClick={() => setQuantity((q) => q + 1)}
                  className="w-10 h-10 border rounded-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                  aria-label={t('product.quantity')}
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Actions — mobilde sticky bar'da; masaüstünde burada */}
            <div className="hidden gap-3 lg:flex">
              <Button
                onClick={handleAddToCart}
                size="lg"
                className="flex-1 bg-black hover:bg-gray-800"
              >
                {t('common.addToCart')}
              </Button>
              <Button
                onClick={handleWishlist}
                variant="outline"
                size="icon"
                className="h-11 w-11 shrink-0"
                aria-label={t('common.wishlist')}
              >
                <Heart size={20} className={inWishlist ? 'fill-red-500 text-red-500' : ''} />
              </Button>
            </div>

            {/* Shipping Info */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Truck size={20} className="text-gray-600" />
                <span className="text-sm">{t('promo.freeShipping')}</span>
              </div>
              <div className="flex items-center gap-3">
                <RefreshCw size={20} className="text-gray-600" />
                <span className="text-sm">{t('home.features.easyReturnsDesc')}</span>
              </div>
              <div className="flex items-center gap-3">
                <Check size={20} className="text-green-600" />
                <span className="text-sm text-green-600">
                  {product.stock > 0 ? t('product.inStock') : t('product.outOfStock')}
                </span>
              </div>
            </div>
            </div>
          </div>

        {/* Product Details Tabs */}
        <div className="mt-16 px-4 sm:px-6 lg:px-0">
          <Tabs defaultValue="description" className="w-full">
            <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0">
              <TabsTrigger
                value="description"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent"
              >
                {t('product.description')}
              </TabsTrigger>
              <TabsTrigger
                value="details"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent"
              >
                {t('product.details')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="description" className="mt-6">
              <div
                className="text-gray-700 leading-relaxed max-w-3xl"
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            </TabsContent>
            <TabsContent value="details" className="mt-6">
              <dl className="space-y-4 max-w-lg">
                {product.material && (
                  <div className="flex">
                    <dt className="font-medium w-32">{t('product.material')}</dt>
                    <dd className="text-gray-700">{product.material}</dd>
                  </div>
                )}
                {product.care && (
                  <div className="flex">
                    <dt className="font-medium w-32">{t('product.care')}</dt>
                    <dd className="text-gray-700">{product.care}</dd>
                  </div>
                )}
                {product.stock !== undefined && (
                  <div className="flex">
                    <dt className="font-medium w-32">Stok</dt>
                    <dd className="text-gray-700">{product.stock} adet</dd>
                  </div>
                )}
              </dl>
            </TabsContent>
          </Tabs>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16 px-4 sm:px-6 lg:px-0">
            <h2 className="mb-3 text-2xl font-bold">{t('product.youMayAlsoLike')}</h2>
            {loading ? (
              <ProductGridSkeleton columns={4} count={4} />
            ) : (
              <ProductCarousel
                title=""
                products={relatedProducts}
                cardVariant="grid"
                showQuickAdd={false}
                embedded
                autoScroll
              />
            )}
          </div>
        )}
        </div>
      </div>
      <MobileStickyBar
        price={formatCurrency(product.price)}
        ctaLabel={t('common.addToCart')}
        onCtaClick={handleAddToCart}
        secondaryAction={{
          onClick: handleWishlist,
          ariaLabel: t('common.wishlist'),
          icon: <Heart size={20} className={inWishlist ? 'fill-red-500 text-red-500' : ''} />,
        }}
      />
    </Layout>
  );
};

export default ProductDetailPage;